import { RfqTemplateType } from '@lune-fe/dapi-models'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import ClientAccountDetails from 'views/CalculateEmissions/RFQ/ClientAccountDetails'
import ShippingDetails from 'views/CalculateEmissions/RFQ/ShippingDetails'
import TemplateLeg from 'views/CalculateEmissions/RFQ/TemplateLeg'
import { getRfqState } from 'views/CalculateEmissions/RFQ/utils/rfqStateStore'
import { RFQState } from 'views/CalculateEmissions/RFQ/utils/types'
import { showClientAccountsStep } from 'views/CalculateEmissions/RFQ/utils/utils'

export type Stage = 'shippingDetails' | 'clientAccountDetails' | 'leg'

export type StageContext = {
    state: RFQState | undefined
    updateState: (newState: RFQState) => void
    navigate: (newStage: Stage) => void
    onSuccess?: () => void
}

type MultiStepFlowProps = {
    sheetType: RfqTemplateType
    context?: StageContext
}

type RouteState = {
    initialStage?: Stage
    stateKey?: string
    context?: { state: RFQState }
}

const MultiStepFlow = ({ sheetType, context: externalContext }: MultiStepFlowProps) => {
    const location = useLocation()
    const routeState = location.state as RouteState | undefined

    // Helper function to retrieve state from the shared store
    const getStateFromStore = (stateKey?: string): RFQState | undefined => {
        if (!stateKey) return undefined
        return getRfqState(stateKey)
    }
    // Initialize state from route state (either directly or from module-level store via stateKey)
    const initialState = routeState?.context?.state ?? getStateFromStore(routeState?.stateKey)
    const [state, setState] = useState<RFQState | undefined>(initialState)

    // Initialize stage from route state or default based on job settings
    // Show client account stage when per_row is selected or template already has clientAccountName
    const showClientAccountStage = showClientAccountsStep(initialState)
    const defaultStage: Stage = showClientAccountStage ? 'clientAccountDetails' : 'shippingDetails'
    const initialStage = routeState?.initialStage ?? defaultStage
    const [stage, setStage] = useState<Stage>(initialStage)

    // Update stage and state when route state changes
    useEffect(() => {
        const currentRouteState = location.state as RouteState | undefined
        if (currentRouteState?.initialStage) {
            setStage(currentRouteState.initialStage)
        }
        // Try to get state from route state or module-level store
        const newState =
            currentRouteState?.context?.state ?? getStateFromStore(currentRouteState?.stateKey)
        if (newState) {
            setState(newState)
        }
    }, [location.state])

    const internalContext: StageContext = {
        state,
        updateState: setState,
        navigate: setStage,
    }

    const context = externalContext ?? internalContext

    const stageToComponent: Record<Stage, React.ReactElement> = {
        clientAccountDetails: <ClientAccountDetails sheetType={sheetType} context={context} />,
        shippingDetails: <ShippingDetails sheetType={sheetType} context={context} />,
        leg: <TemplateLeg sheetType={sheetType} context={context} />,
    }
    return stageToComponent[stage]
}

export default MultiStepFlow
